import authSession from "../../lib/authSession";
import { API_URL } from "../../constants";
import { safeFetch, explainApiError } from "../../utils/toDisplayString";

/**
 * resourcePersonLessonStore — course structure and lesson child-content authoring.
 *
 * Categories:
 *   GET  /api/resource-person/categories                    — category dropdown
 *   POST /api/resource-person/categories  { name, description?, image? }
 *
 * Lessons (draft courses only; submitted courses return 409 on mutation):
 *   GET    /api/resource-person/courses/:id/lessons
 *   POST   /api/resource-person/courses/:id/lessons
 *   PATCH  /api/resource-person/courses/:id/lessons/:lessonId
 *   DELETE /api/resource-person/courses/:id/lessons/:lessonId
 *   PATCH  /api/resource-person/courses/:id/lessons/reorder  { lessonIds: [] }
 *
 * Modules (group lessons within a draft course):
 *   GET    /api/resource-person/courses/:id/modules
 *   POST   /api/resource-person/courses/:id/modules       { title, description?, position }
 *   PATCH  /api/resource-person/courses/:id/modules/:moduleId
 *   DELETE /api/resource-person/courses/:id/modules/:moduleId   (empty modules only)
 *   PATCH  /api/resource-person/courses/:id/modules/reorder { moduleIds: [] }
 *
 * Assessments, questions, resources and videos use the explicit child routes
 * documented in the Postman collection.
 */

export const LESSON_TYPES = [
  "VIDEO",
  "DOCUMENT",
  "RICH_TEXT",
  "QUIZ",
  "ASSIGNMENT",
  "PRACTICAL",
  "REPORT",
];

export const QUESTION_TYPES = ["MULTIPLE_CHOICE", "TRUE_FALSE", "SHORT_ANSWER"];

export const resourcePersonLessonInitialState = {
  lessons: [],
  lessonsLoading: false,
  lessonsError: null,
  lessonMutationLoading: false,
  lessonMutationError: null,

  questions: [],
  questionsLoading: false,
  questionsError: null,
  questionMutationLoading: false,
  questionMutationError: null,

  assessments: [],
  assessmentsLoading: false,
  assessmentsError: null,
  assessmentMutationLoading: false,
  assessmentMutationError: null,

  lessonResources: [],
  lessonResourcesLoading: false,
  lessonResourcesError: null,
  lessonVideos: [],
  lessonVideosLoading: false,
  lessonVideosError: null,
  lessonMediaMutationLoading: false,
  lessonMediaMutationError: null,

  modules: [],
  modulesLoading: false,
  modulesError: null,
  moduleMutationLoading: false,
  moduleMutationError: null,

  rpCategories: [],
  rpCategoriesLoading: false,
  rpCategoriesError: null,
  rpCategoryMutationLoading: false,
  rpCategoryMutationError: null,
};

function buildUrl(path, params) {
  const query = new URLSearchParams(
    Object.entries(params || {}).filter(([, value]) => value !== undefined && value !== "")
  ).toString();
  return `${API_URL.replace(/\/$/, "")}${path}${query ? `?${query}` : ""}`;
}

function requestOptions(method = "GET", body) {
  const token = authSession.getToken();
  const headers = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (token) headers.Authorization = `Bearer ${token}`;
  return {
    method,
    headers,
    credentials: "omit",
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  };
}

async function requestLessonApi(path, options = {}) {
  if (!API_URL) throw new Error("REACT_APP_API_BASE_URL is not configured.");
  const response = await safeFetch(buildUrl(path, options.params), requestOptions(options.method, options.body));
  const text = await response.text();
  let data = {};
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      if (text.trim().startsWith("<")) throw new Error(`Lesson API returned HTML (${response.status}).`);
      throw new Error(`Lesson API returned a non-JSON response (${response.status}).`);
    }
  }
  if (!response.ok) {
    const error = new Error(explainApiError(response.status, data || {}, `Request failed with status ${response.status}.`));
    error.status = response.status;
    error.payload = data;
    throw error;
  }
  return data;
}

function listOf(data, keys) {
  const payload =
    data?.data && typeof data.data === "object" && !Array.isArray(data.data) ? data.data : data || {};
  for (const key of keys) {
    if (Array.isArray(payload[key])) return payload[key];
  }
  return Array.isArray(payload) ? payload : [];
}

/**
 * Unwrap the created/updated entity from any of the envelopes the API uses.
 *
 * Shapes seen from the backend:  { id, … } · { module: { id, … } } ·
 * { data: { id, … } } · { data: { module: { id, … } } } · { message, module }.
 * The old version stopped after one unwrap, so `{ data: { module: … } }`
 * returned the whole envelope and callers read `envelope.id` as undefined —
 * the row rendered with a synthetic `new-…` id and then vanished on refetch.
 */
function payloadOf(data) {
  const ENTITY_KEYS = [
    "lesson",
    "question",
    "assessment",
    "resource",
    "lessonResource",
    "video",
    "lessonVideo",
    "category",
    "module",
    "course",
    "item",
  ];
  let value = data;
  // Peel up to two envelope layers (`data`) and one entity wrapper (`module`…).
  for (let depth = 0; depth < 3; depth += 1) {
    if (!value || typeof value !== "object" || Array.isArray(value)) break;
    if (value.data && typeof value.data === "object" && !Array.isArray(value.data)) {
      value = value.data;
      continue;
    }
    // Only unwrap an entity wrapper while we have no entity yet. Once the
    // payload already carries its own id it IS the entity — e.g. a lesson
    // returned flat with a nested `category` object, which must not be
    // mistaken for a `{ category: … }` envelope.
    if (value.id === undefined && value._id === undefined) {
      const key = ENTITY_KEYS.find(
        (k) => value[k] && typeof value[k] === "object" && !Array.isArray(value[k])
      );
      if (key) {
        value = value[key];
        continue;
      }
    }
    break;
  }
  return value && typeof value === "object" ? value : {};
}

function sortByPosition(items) {
  return [...items].sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0));
}

export function createResourcePersonLessonActions(set, get) {
  const failLesson = (error) => {
    set({
      lessonsLoading: false,
      lessonsError: error.message,
      lessonMutationLoading: false,
      lessonMutationError: error.message,
    });
    return { success: false, error: error.message, status: error.status };
  };

  const failModule = (error) => {
    set({
      modulesLoading: false,
      modulesError: error.message,
      moduleMutationLoading: false,
      moduleMutationError: error.message,
    });
    return { success: false, error: error.message, status: error.status };
  };

  return {
    // GET /api/resource-person/categories (dropdown source)
    fetchRpCategories: async (params = {}) => {
      set({ rpCategoriesLoading: true, rpCategoriesError: null });
      try {
        const data = await requestLessonApi("/api/resource-person/categories", { params });
        const categories = listOf(data, ["categories", "items", "data"]);
        set({ rpCategoriesLoading: false, rpCategories: categories });
        return { success: true, categories };
      } catch (error) {
        set({ rpCategoriesLoading: false, rpCategoriesError: error.message });
        return { success: false, error: error.message, status: error.status };
      }
    },

    // POST /api/resource-person/categories { name, description?, image? }
    createRpCategory: async (payload) => {
      set({ rpCategoryMutationLoading: true, rpCategoryMutationError: null });
      try {
        const data = await requestLessonApi("/api/resource-person/categories", {
          method: "POST",
          body: payload,
        });
        const category = payloadOf(data);
        // Optimistic insert so the new category appears in the dropdown even if
        // the API returns 201 with no body; the builder then selects it by id.
        const created = {
          ...payload,
          ...category,
          id: category?.id ?? category?._id ?? `new-${Date.now()}`,
        };
        set((state) => ({
          rpCategoryMutationLoading: false,
          rpCategories: [created, ...(state.rpCategories || [])],
        }));
        return { success: true, category: created };
      } catch (error) {
        set({ rpCategoryMutationLoading: false, rpCategoryMutationError: error.message });
        return { success: false, error: error.message, status: error.status };
      }
    },

    // GET /api/resource-person/courses/:id/modules
    fetchModules: async (courseId, params = {}) => {
      set({ modulesLoading: true, modulesError: null });
      try {
        const data = await requestLessonApi(`/api/resource-person/courses/${courseId}/modules`, {
          params,
        });
        const modules = sortByPosition(listOf(data, ["modules", "items", "data"]));
        set({ modulesLoading: false, modules });
        return { success: true, modules };
      } catch (error) {
        // Keep whatever modules are already on screen. Blanking the list here is
        // what made modules "disappear": any transient failure (a parallel
        // request, an auth blip) wiped a list the author had already built.
        set({ modulesLoading: false, modulesError: error.message });
        return { success: false, error: error.message, status: error.status };
      }
    },

    // Read-only module count. Unlike fetchModules this NEVER writes to the shared
    // `modules` slice, so it is safe to call for many courses at once (the library
    // grid does exactly that). fetchModules overwrites global state, which used to
    // replace the open builder's module list with another course's modules.
    fetchModuleCount: async (courseId) => {
      try {
        const data = await requestLessonApi(`/api/resource-person/courses/${courseId}/modules`, {});
        return { success: true, count: listOf(data, ["modules", "items", "data"]).length };
      } catch (error) {
        return { success: false, error: error.message, count: 0 };
      }
    },

    // POST /api/resource-person/courses/:id/modules { title, description?, position }
    createModule: async (courseId, payload) => {
      set({ moduleMutationLoading: true, moduleMutationError: null });
      try {
        const data = await requestLessonApi(`/api/resource-person/courses/${courseId}/modules`, {
          method: "POST",
          body: payload,
        });
        const module = payloadOf(data);
        const created = {
          ...payload,
          ...module,
          id: module?.id ?? module?._id ?? `new-${Date.now()}`,
        };
        set((state) => ({
          moduleMutationLoading: false,
          modules: sortByPosition([...(state.modules || []), created]),
        }));

        // Reconcile so the module picks up its real server id — critical when a
        // lesson is about to be attached to it via moduleId.
        const refetch = await get().fetchModules(courseId);
        if (refetch.success) {
          const serverModules = refetch.modules || [];
          const persisted =
            module?.id != null
              ? serverModules.some((m) => m?.id === module.id)
              : serverModules.length > 0;
          if (!persisted) {
            const msg = "Module was NOT saved by the server. Check the course is in DRAFT/PENDING and try again.";
            set({ moduleMutationError: msg });
            return { success: false, error: msg, status: 422 };
          }
          const realModule =
            module?.id != null
              ? serverModules.find((m) => m?.id === module.id)
              : serverModules.find((m) => m?.title === created.title);
          return { success: true, module: realModule || created };
        }
        return { success: true, module: created };
      } catch (error) {
        return failModule(error);
      }
    },

    // PATCH /api/resource-person/courses/:id/modules/:moduleId
    updateModule: async (courseId, moduleId, payload) => {
      set({ moduleMutationLoading: true, moduleMutationError: null });
      try {
        const data = await requestLessonApi(
          `/api/resource-person/courses/${courseId}/modules/${moduleId}`,
          { method: "PATCH", body: payload }
        );
        const module = payloadOf(data);
        set((state) => ({
          moduleMutationLoading: false,
          modules: (state.modules || []).map((item) =>
            item?.id === moduleId ? { ...item, ...(module || {}), id: moduleId } : item
          ),
        }));
        return { success: true, module };
      } catch (error) {
        return failModule(error);
      }
    },

    // DELETE /api/resource-person/courses/:id/modules/:moduleId (empty only)
    deleteModule: async (courseId, moduleId) => {
      set({ moduleMutationLoading: true, moduleMutationError: null });
      try {
        await requestLessonApi(
          `/api/resource-person/courses/${courseId}/modules/${moduleId}`,
          { method: "DELETE" }
        );
        set((state) => ({
          moduleMutationLoading: false,
          modules: (state.modules || []).filter((item) => item?.id !== moduleId),
        }));
        return { success: true };
      } catch (error) {
        return failModule(error);
      }
    },

    // PATCH /api/resource-person/courses/:id/modules/reorder { moduleIds: [] }
    reorderModules: async (courseId, moduleIds) => {
      set({ moduleMutationLoading: true, moduleMutationError: null });
      try {
        await requestLessonApi(`/api/resource-person/courses/${courseId}/modules/reorder`, {
          method: "PATCH",
          body: { moduleIds },
        });
        // Reflect the requested order locally without a refetch.
        set((state) => {
          const byId = new Map((state.modules || []).map((m) => [m?.id, m]));
          const ordered = (moduleIds || [])
            .map((id, index) => {
              const m = byId.get(id);
              return m ? { ...m, position: index + 1 } : null;
            })
            .filter(Boolean);
          return { moduleMutationLoading: false, modules: ordered };
        });
        return { success: true };
      } catch (error) {
        return failModule(error);
      }
    },

    // GET /api/resource-person/courses/:id/lessons
    fetchLessons: async (courseId, params = {}) => {
      set({ lessonsLoading: true, lessonsError: null });
      try {
        const data = await requestLessonApi(`/api/resource-person/courses/${courseId}/lessons`, {
          params,
        });
        const lessons = sortByPosition(listOf(data, ["lessons", "items", "data"]));
        set({ lessonsLoading: false, lessons, questions: [] });
        return { success: true, lessons };
      } catch (error) {
        // As with modules: never blank the list on a failed read — a transient
        // failure must not erase lessons the author already created.
        set({ lessonsLoading: false, lessonsError: error.message });
        return { success: false, error: error.message, status: error.status };
      }
    },

    fetchLessonCount: async (courseId) => {
      try {
        const data = await requestLessonApi(`/api/resource-person/courses/${courseId}/lessons`, {});
        return {success: true, count: listOf(data, ["lessons", "items", "data"]).length};
      } catch (error) {
        return {success: false, error: error.message};
      }
    },

    // POST /api/resource-person/courses/:id/lessons
    createLesson: async (courseId, payload) => {
      set({ lessonMutationLoading: true, lessonMutationError: null });
      try {
        const data = await requestLessonApi(`/api/resource-person/courses/${courseId}/lessons`, {
          method: "POST",
          body: payload,
        });
        const lesson = payloadOf(data);
        // Optimistic entry — used for immediate UI feedback. Real id may be
        // missing if the API returns 201 with no body; we reconcile below.
        const created = {
          ...payload,
          ...lesson,
          id: lesson?.id ?? lesson?._id ?? `new-${Date.now()}`,
        };
        set((state) => ({
          lessonMutationLoading: false,
          lessons: sortByPosition([...(state.lessons || []), created]),
        }));

        // Reconcile with the server: if the API persisted the lesson, GET
        // returns it with its real id. If the API returned 2xx but did NOT
        // persist, we surface an error instead of silently showing 0.
        const refetch = await get().fetchLessons(courseId);
        if (!refetch.success) {
          // Network failed during reconcile — keep optimistic entry.
          return { success: true, lesson: created };
        }
        const serverLessons = refetch.lessons || [];
        // The list GET may not echo `moduleId` (or may omit it on some builds);
        // re-apply any moduleId the client just sent so the grouping stays
        // accurate until the backend includes it.
        if (payload.moduleId) {
          const patched = serverLessons.map((l) =>
            l?.id === created.id && !l?.moduleId && !l?.module_id
              ? { ...l, moduleId: payload.moduleId }
              : l
          );
          set({ lessons: sortByPosition(patched) });
        }
        const persisted =
          lesson?.id != null
            ? serverLessons.some((l) => l?.id === lesson.id)
            : serverLessons.length > 0;
        if (!persisted) {
          const msg =
            "Lesson was NOT saved by the server. Check that this course is in DRAFT/PENDING status and try again.";
          set({ lessonMutationError: msg });
          return { success: false, error: msg, status: 422 };
        }
        return { success: true, lesson: created };
      } catch (error) {
        return failLesson(error);
      }
    },

    // PATCH /api/resource-person/courses/:id/lessons/:lessonId
    updateLesson: async (courseId, lessonId, payload) => {
      set({ lessonMutationLoading: true, lessonMutationError: null });
      try {
        const data = await requestLessonApi(
          `/api/resource-person/courses/${courseId}/lessons/${lessonId}`,
          { method: "PATCH", body: payload }
        );
        const lesson = payloadOf(data);
        set((state) => ({
          lessonMutationLoading: false,
          lessons: (state.lessons || []).map((item) =>
            item?.id === lessonId
              // Server response wins, but never let it drop a field the client
              // explicitly sent (e.g. moduleId when the API doesn't echo it).
              ? { ...item, ...payload, ...(lesson || {}), id: lessonId }
              : item
          ),
        }));
        return { success: true, lesson };
      } catch (error) {
        return failLesson(error);
      }
    },

    // DELETE /api/resource-person/courses/:id/lessons/:lessonId
    deleteLesson: async (courseId, lessonId) => {
      set({ lessonMutationLoading: true, lessonMutationError: null });
      try {
        await requestLessonApi(`/api/resource-person/courses/${courseId}/lessons/${lessonId}`, {
          method: "DELETE",
        });
        set((state) => ({
          lessonMutationLoading: false,
          lessons: (state.lessons || []).filter((item) => item?.id !== lessonId),
        }));
        return { success: true };
      } catch (error) {
        return failLesson(error);
      }
    },

    // PATCH .../lessons/reorder { lessonIds: [] }
    reorderLessons: async (courseId, moduleId, lessonIds) => {
      if (!moduleId) {
        const error = new Error("A module is required to reorder its lessons.");
        return failLesson(error);
      }
      set({ lessonMutationLoading: true, lessonMutationError: null });
      try {
        const data = await requestLessonApi(
          `/api/resource-person/courses/${courseId}/lessons/reorder`,
          { method: "PATCH", body: { moduleId, lessonIds } }
        );
        const lessons = listOf(data, ["lessons", "items", "data"]);
        set((state) => {
          if (lessons.length) {
            return { lessonMutationLoading: false, lessons: sortByPosition(lessons) };
          }
          // Server may not echo the list — mirror the requested order locally.
          const byId = new Map((state.lessons || []).map((item) => [item?.id, item]));
          const ordered = (lessonIds || []).map((id, index) => ({
            ...(byId.get(id) || { id }),
            position: index + 1,
          }));
          byId.forEach((item) => {
            if (!(lessonIds || []).includes(item?.id)) ordered.push(item);
          });
          return { lessonMutationLoading: false, lessons: ordered };
        });
        return { success: true };
      } catch (error) {
        return failLesson(error);
      }
    },

    fetchAssessments: async (courseId, lessonId) => {
      set({ assessmentsLoading: true, assessmentsError: null });
      try {
        const data = await requestLessonApi(
          `/api/resource-person/courses/${courseId}/lessons/${lessonId}/assessments`
        );
        const assessments = sortByPosition(listOf(data, ["assessments", "items", "data"]));
        set({ assessmentsLoading: false, assessments });
        return { success: true, assessments };
      } catch (error) {
        set({ assessmentsLoading: false, assessmentsError: error.message });
        return { success: false, error: error.message, status: error.status };
      }
    },

    createAssessment: async (courseId, lessonId, payload) => {
      set({ assessmentMutationLoading: true, assessmentMutationError: null });
      try {
        let data;
        try {
          data = await requestLessonApi(
            `/api/resource-person/courses/${courseId}/lessons/${lessonId}/assessments`,
            { method: "POST", body: payload }
          );
        } catch (error) {
          // Some deployments validate the create body with `type` while the
          // response (and DB) uses `kind`. Retry once with the accepted key.
          if (!/unrecognized key[\s\S]*kind/i.test(error?.message || "") || !("kind" in (payload || {}))) {
            throw error;
          }
          const { kind, ...rest } = payload;
          data = await requestLessonApi(
            `/api/resource-person/courses/${courseId}/lessons/${lessonId}/assessments`,
            { method: "POST", body: { ...rest, type: kind } }
          );
        }
        const assessment = payloadOf(data);
        const created = {
          ...payload,
          ...assessment,
          id: assessment?.id ?? assessment?._id ?? `new-${Date.now()}`,
        };
        set((state) => ({
          assessmentMutationLoading: false,
          assessments: sortByPosition([
            ...(state.assessments || []),
            created,
          ]),
        }));
        return { success: true, assessment: created };
      } catch (error) {
        set({ assessmentMutationLoading: false, assessmentMutationError: error.message });
        return { success: false, error: error.message, status: error.status };
      }
    },

    updateAssessment: async (courseId, lessonId, assessmentId, payload) => {
      set({ assessmentMutationLoading: true, assessmentMutationError: null });
      try {
        let data;
        try {
          data = await requestLessonApi(
            `/api/resource-person/courses/${courseId}/lessons/${lessonId}/assessments/${assessmentId}`,
            { method: "PATCH", body: payload }
          );
        } catch (error) {
          if (!/unrecognized key[\s\S]*kind/i.test(error?.message || "") || !("kind" in (payload || {}))) {
            throw error;
          }
          const { kind, ...rest } = payload;
          data = await requestLessonApi(
            `/api/resource-person/courses/${courseId}/lessons/${lessonId}/assessments/${assessmentId}`,
            { method: "PATCH", body: { ...rest, type: kind } }
          );
        }
        const assessment = payloadOf(data);
        set((state) => ({
          assessmentMutationLoading: false,
          assessments: (state.assessments || []).map((item) =>
            item?.id === assessmentId ? { ...item, ...payload, ...(assessment || {}), id: assessmentId } : item
          ),
        }));
        return { success: true, assessment };
      } catch (error) {
        set({ assessmentMutationLoading: false, assessmentMutationError: error.message });
        return { success: false, error: error.message, status: error.status };
      }
    },

    deleteAssessment: async (courseId, lessonId, assessmentId) => {
      set({ assessmentMutationLoading: true, assessmentMutationError: null });
      try {
        await requestLessonApi(
          `/api/resource-person/courses/${courseId}/lessons/${lessonId}/assessments/${assessmentId}`,
          { method: "DELETE" }
        );
        set((state) => ({
          assessmentMutationLoading: false,
          assessments: (state.assessments || []).filter((item) => item?.id !== assessmentId),
        }));
        return { success: true };
      } catch (error) {
        set({ assessmentMutationLoading: false, assessmentMutationError: error.message });
        return { success: false, error: error.message, status: error.status };
      }
    },

    reorderAssessments: async (courseId, lessonId, assessmentIds) => {
      set({ assessmentMutationLoading: true, assessmentMutationError: null });
      try {
        const data = await requestLessonApi(
          `/api/resource-person/courses/${courseId}/lessons/${lessonId}/assessments/reorder`,
          { method: "PATCH", body: { assessmentIds } }
        );
        const assessments = listOf(data, ["assessments", "items", "data"]);
        set((state) => ({
          assessmentMutationLoading: false,
          assessments: sortByPosition(
            assessments.length
              ? assessments
              : assessmentIds.map((id, position) => ({
                  ...(state.assessments || []).find((item) => item.id === id),
                  id,
                  position: position + 1,
                }))
          ),
        }));
        return { success: true };
      } catch (error) {
        set({ assessmentMutationLoading: false, assessmentMutationError: error.message });
        return { success: false, error: error.message, status: error.status };
      }
    },

    fetchQuestions: async (courseId, lessonId, assessmentId) => {
      set({ questionsLoading: true, questionsError: null });
      try {
        const data = await requestLessonApi(
          `/api/resource-person/courses/${courseId}/lessons/${lessonId}/assessments/${assessmentId}/questions`
        );
        const questions = sortByPosition(listOf(data, ["questions", "items", "data"]));
        set({ questionsLoading: false, questions });
        return { success: true, questions };
      } catch (error) {
        set({ questionsLoading: false, questionsError: error.message, questions: [] });
        return { success: false, error: error.message, status: error.status };
      }
    },

    createQuestion: async (courseId, lessonId, assessmentId, payload) => {
      set({ questionMutationLoading: true, questionMutationError: null });
      try {
        const data = await requestLessonApi(
          `/api/resource-person/courses/${courseId}/lessons/${lessonId}/assessments/${assessmentId}/questions`,
          { method: "POST", body: payload }
        );
        const question = payloadOf(data);
        // Keep an optimistic entry when the API returns 201 with no body so the
        // new question is visible immediately; the payload always has a prompt.
        const created = {
          ...payload,
          ...question,
          id: question?.id ?? question?._id ?? `new-${Date.now()}`,
        };
        set((state) => ({
          questionMutationLoading: false,
          questions: sortByPosition([...(state.questions || []), created]),
        }));
        return { success: true, question: created };
      } catch (error) {
        set({ questionMutationLoading: false, questionMutationError: error.message });
        return { success: false, error: error.message, status: error.status };
      }
    },

    // PATCH .../questions/:questionId
    updateQuestion: async (courseId, lessonId, assessmentId, questionId, payload) => {
      set({ questionMutationLoading: true, questionMutationError: null });
      try {
        const data = await requestLessonApi(
          `/api/resource-person/courses/${courseId}/lessons/${lessonId}/assessments/${assessmentId}/questions/${questionId}`,
          { method: "PATCH", body: payload }
        );
        const question = payloadOf(data);
        set((state) => ({
          questionMutationLoading: false,
          questions: (state.questions || []).map((item) =>
            item?.id === questionId ? { ...item, ...payload, ...(question || {}), id: questionId } : item
          ),
        }));
        return { success: true, question };
      } catch (error) {
        set({ questionMutationLoading: false, questionMutationError: error.message });
        return { success: false, error: error.message, status: error.status };
      }
    },

    // DELETE .../questions/:questionId
    deleteQuestion: async (courseId, lessonId, assessmentId, questionId) => {
      set({ questionMutationLoading: true, questionMutationError: null });
      try {
        await requestLessonApi(
          `/api/resource-person/courses/${courseId}/lessons/${lessonId}/assessments/${assessmentId}/questions/${questionId}`,
          { method: "DELETE" }
        );
        set((state) => ({
          questionMutationLoading: false,
          questions: (state.questions || []).filter((item) => item?.id !== questionId),
        }));
        return { success: true };
      } catch (error) {
        set({ questionMutationLoading: false, questionMutationError: error.message });
        return { success: false, error: error.message, status: error.status };
      }
    },

    fetchLessonResources: async (courseId, lessonId) => {
      set({ lessonResourcesLoading: true, lessonResourcesError: null });
      try {
        const data = await requestLessonApi(
          `/api/resource-person/courses/${courseId}/lessons/${lessonId}/resources`
        );
        const lessonResources = sortByPosition(listOf(data, ["resources", "items", "data"]));
        set({ lessonResourcesLoading: false, lessonResources });
        return { success: true, resources: lessonResources };
      } catch (error) {
        set({ lessonResourcesLoading: false, lessonResourcesError: error.message });
        return { success: false, error: error.message, status: error.status };
      }
    },

    createLessonResource: async (courseId, lessonId, payload) => {
      set({ lessonMediaMutationLoading: true, lessonMediaMutationError: null });
      try {
        const data = await requestLessonApi(
          `/api/resource-person/courses/${courseId}/lessons/${lessonId}/resources`,
          { method: "POST", body: payload }
        );
        const resource = payloadOf(data);
        set((state) => ({
          lessonMediaMutationLoading: false,
          lessonResources: sortByPosition([
            ...(state.lessonResources || []),
            { ...payload, ...resource, id: resource?.id ?? resource?._id ?? `new-${Date.now()}` },
          ]),
        }));
        return { success: true, resource };
      } catch (error) {
        set({ lessonMediaMutationLoading: false, lessonMediaMutationError: error.message });
        return { success: false, error: error.message, status: error.status };
      }
    },

    updateLessonResource: async (courseId, lessonId, resourceId, payload) => {
      set({ lessonMediaMutationLoading: true, lessonMediaMutationError: null });
      try {
        const data = await requestLessonApi(
          `/api/resource-person/courses/${courseId}/lessons/${lessonId}/resources/${resourceId}`,
          { method: "PATCH", body: payload }
        );
        const resource = payloadOf(data);
        set((state) => ({
          lessonMediaMutationLoading: false,
          lessonResources: (state.lessonResources || []).map((item) =>
            item?.id === resourceId ? { ...item, ...payload, ...(resource || {}), id: resourceId } : item
          ),
        }));
        return { success: true, resource };
      } catch (error) {
        set({ lessonMediaMutationLoading: false, lessonMediaMutationError: error.message });
        return { success: false, error: error.message, status: error.status };
      }
    },

    deleteLessonResource: async (courseId, lessonId, resourceId) => {
      set({ lessonMediaMutationLoading: true, lessonMediaMutationError: null });
      try {
        await requestLessonApi(
          `/api/resource-person/courses/${courseId}/lessons/${lessonId}/resources/${resourceId}`,
          { method: "DELETE" }
        );
        set((state) => ({
          lessonMediaMutationLoading: false,
          lessonResources: (state.lessonResources || []).filter((item) => item?.id !== resourceId),
        }));
        return { success: true };
      } catch (error) {
        set({ lessonMediaMutationLoading: false, lessonMediaMutationError: error.message });
        return { success: false, error: error.message, status: error.status };
      }
    },

    reorderLessonResources: async (courseId, lessonId, resourceIds) => {
      set({ lessonMediaMutationLoading: true, lessonMediaMutationError: null });
      try {
        const data = await requestLessonApi(
          `/api/resource-person/courses/${courseId}/lessons/${lessonId}/resources/reorder`,
          { method: "PATCH", body: { resourceIds } }
        );
        const resources = listOf(data, ["resources", "items", "data"]);
        set((state) => ({
          lessonMediaMutationLoading: false,
          lessonResources: sortByPosition(
            resources.length
              ? resources
              : resourceIds.map((id, position) => ({
                  ...(state.lessonResources || []).find((item) => item.id === id),
                  id,
                  position: position + 1,
                }))
          ),
        }));
        return { success: true };
      } catch (error) {
        set({ lessonMediaMutationLoading: false, lessonMediaMutationError: error.message });
        return { success: false, error: error.message, status: error.status };
      }
    },

    fetchLessonVideos: async (courseId, lessonId) => {
      set({ lessonVideosLoading: true, lessonVideosError: null });
      try {
        const data = await requestLessonApi(
          `/api/resource-person/courses/${courseId}/lessons/${lessonId}/videos`
        );
        const lessonVideos = sortByPosition(listOf(data, ["videos", "items", "data"]));
        set({ lessonVideosLoading: false, lessonVideos });
        return { success: true, videos: lessonVideos };
      } catch (error) {
        set({ lessonVideosLoading: false, lessonVideosError: error.message });
        return { success: false, error: error.message, status: error.status };
      }
    },

    createLessonVideo: async (courseId, lessonId, payload) => {
      set({ lessonMediaMutationLoading: true, lessonMediaMutationError: null });
      try {
        const data = await requestLessonApi(
          `/api/resource-person/courses/${courseId}/lessons/${lessonId}/videos`,
          { method: "POST", body: payload }
        );
        const video = payloadOf(data);
        set((state) => ({
          lessonMediaMutationLoading: false,
          lessonVideos: sortByPosition([
            ...(state.lessonVideos || []),
            { ...payload, ...video, id: video?.id ?? video?._id ?? `new-${Date.now()}` },
          ]),
        }));
        return { success: true, video };
      } catch (error) {
        set({ lessonMediaMutationLoading: false, lessonMediaMutationError: error.message });
        return { success: false, error: error.message, status: error.status };
      }
    },

    updateLessonVideo: async (courseId, lessonId, videoId, payload) => {
      set({ lessonMediaMutationLoading: true, lessonMediaMutationError: null });
      try {
        const data = await requestLessonApi(
          `/api/resource-person/courses/${courseId}/lessons/${lessonId}/videos/${videoId}`,
          { method: "PATCH", body: payload }
        );
        const video = payloadOf(data);
        set((state) => ({
          lessonMediaMutationLoading: false,
          lessonVideos: (state.lessonVideos || []).map((item) =>
            item?.id === videoId ? { ...item, ...payload, ...(video || {}), id: videoId } : item
          ),
        }));
        return { success: true, video };
      } catch (error) {
        set({ lessonMediaMutationLoading: false, lessonMediaMutationError: error.message });
        return { success: false, error: error.message, status: error.status };
      }
    },

    deleteLessonVideo: async (courseId, lessonId, videoId) => {
      set({ lessonMediaMutationLoading: true, lessonMediaMutationError: null });
      try {
        await requestLessonApi(
          `/api/resource-person/courses/${courseId}/lessons/${lessonId}/videos/${videoId}`,
          { method: "DELETE" }
        );
        set((state) => ({
          lessonMediaMutationLoading: false,
          lessonVideos: (state.lessonVideos || []).filter((item) => item?.id !== videoId),
        }));
        return { success: true };
      } catch (error) {
        set({ lessonMediaMutationLoading: false, lessonMediaMutationError: error.message });
        return { success: false, error: error.message, status: error.status };
      }
    },

    reorderLessonVideos: async (courseId, lessonId, videoIds) => {
      set({ lessonMediaMutationLoading: true, lessonMediaMutationError: null });
      try {
        const data = await requestLessonApi(
          `/api/resource-person/courses/${courseId}/lessons/${lessonId}/videos/reorder`,
          { method: "PATCH", body: { videoIds } }
        );
        const videos = listOf(data, ["videos", "items", "data"]);
        set((state) => ({
          lessonMediaMutationLoading: false,
          lessonVideos: sortByPosition(
            videos.length
              ? videos
              : videoIds.map((id, position) => ({
                  ...(state.lessonVideos || []).find((item) => item.id === id),
                  id,
                  position: position + 1,
                }))
          ),
        }));
        return { success: true };
      } catch (error) {
        set({ lessonMediaMutationLoading: false, lessonMediaMutationError: error.message });
        return { success: false, error: error.message, status: error.status };
      }
    },

    clearLessonAuthoring: () =>
      set({
        lessons: [],
        lessonsError: null,
        lessonMutationError: null,
        questions: [],
        questionsError: null,
        questionMutationError: null,
        assessments: [],
        assessmentsError: null,
        assessmentMutationError: null,
        lessonResources: [],
        lessonResourcesError: null,
        lessonVideos: [],
        lessonVideosError: null,
        lessonMediaMutationError: null,
        modules: [],
        modulesError: null,
        moduleMutationError: null,
      }),
  };
}

const resourcePersonLessonActions = createResourcePersonLessonActions;
export default resourcePersonLessonActions;
